/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useCallback, useMemo, useRef } from 'react'
import { TextInput } from 'src/components/ts-components/TextInput'
import Text from 'src/components/ts-components/Text'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import { Country } from '@sportninja/common/hooks/useCountries'
import { Province } from 'src/components/ts-components/ProvinceSelectInput'
import { Alert } from 'antd'
import useWindowSize from 'src/hooks/useWindowSize'
import { useEditAccountContext } from './EditAccountContext'

const fieldWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EditAccountModalContent: React.FC = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { state, dispatch } = useEditAccountContext()

  const countriesRef = useRef<Country[]>([])
  const provincesRef = useRef<Province[]>([])

  const handleFieldChange = useCallback(
    (field: string) => (value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field,
        value,
      })
    },
    [dispatch]
  )

  const handlePhoneChange = useCallback(
    (value: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number',
        value: value || '',
      })
    },
    [dispatch]
  )

  const handlePhoneCountryChange = useCallback(
    (country: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number_country',
        value: country || '',
      })
    },
    [dispatch]
  )

  const handleCountryChange = useCallback(
    (value: string | undefined, countryId: string | number | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'address_country_iso',
        value: value || '',
      })
      dispatch({
        type: 'SET_FIELD',
        field: 'address_country_id',
        value: countryId ? Number(countryId) : undefined,
      })
    },
    [dispatch]
  )

  const handleProvinceChange = useCallback(
    (value: string | undefined, provinceId: string | number | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'address_province_iso',
        value: value || '',
      })
      dispatch({
        type: 'SET_FIELD',
        field: 'address_province_id',
        value: provinceId ? Number(provinceId) : undefined,
      })
    },
    [dispatch]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        height: 100%;
        overflow-y: auto;
        width: 100%;
      `}
    >
      {/* Section: Contact Info */}
      <Text variant='captionLarge' weight='semiBold'>
        Contact Info
      </Text>

      {/* Full Name + Title */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='full_name'
            label='Full Name'
            value={state.full_name}
            setValue={handleFieldChange('full_name')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Full Name'
            required
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='title'
            label='Title'
            value={state.title}
            setValue={handleFieldChange('title')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Title'
            required
          />
        </div>
      </div>

      {/* Phone + Email */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <ModernPhoneInput
            value={state.phone_number}
            onChange={handlePhoneChange}
            onCountryChange={handlePhoneCountryChange}
            label='Phone Number'
            error={
              state.fieldErrors?.includes('phone_number')
                ? (state.customError?.phone_number as string) ||
                  'Invalid phone number'
                : ''
            }
            validateOnBlur={false}
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='email'
            label='Email'
            value={state.email}
            setValue={handleFieldChange('email')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Email Address'
            type='email'
            disabled
            required
          />
        </div>
      </div>

      {/* Company Name */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='company_name'
          label='Company Name'
          value={state.company_name}
          setValue={handleFieldChange('company_name')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          placeholder='Company Name'
          required
        />
      </div>

      {/* Divider */}
      <div
        css={css`
          height: 1px;
          background-color: ${colors.SECONDARY_100};
          width: 100%;
        `}
      />

      {/* Section: Address */}
      <Text variant='captionSmall' weight='semiBold' color={colors.NEUTRAL_50}>
        Address
      </Text>

      {/* Street */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='address_street'
          label='Street'
          value={state.address_street}
          setValue={handleFieldChange('address_street')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          placeholder='Street'
        />
      </div>

      {/* City + Province */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='address_city'
            label='City'
            value={state.address_city}
            setValue={handleFieldChange('address_city')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='City'
          />
        </div>
      </div>

      {/* Country + Province */}
      <CountryAndProvinceInput
        allowClear
        countriesRef={countriesRef}
        provincesRef={provincesRef}
        countryValue={state.address_country_iso}
        provinceValue={state.address_province_iso}
        onCountryChange={handleCountryChange}
        onProvinceChange={handleProvinceChange}
        valueMode='iso_code'
        provinceValueMode='iso_code'
        initialCountryId={state.address_country_id}
        labels={{
          country: 'Country',
          province: 'State/Province',
        }}
        placeholders={{
          country: 'Select a country',
          province: 'Select state/province',
        }}
      />

      {/* Postal Code */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='address_postal_code'
          label='ZIP/Postal Code'
          value={state.address_postal_code}
          setValue={handleFieldChange('address_postal_code')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          placeholder='ZIP/Postal Code'
        />
      </div>

      {/* Error Display */}
      {state.error && (
        <Alert
          message={state.error}
          type='error'
          showIcon
          css={css`
            background: ${colors.ERROR_50};
            border: 1px solid ${colors.ERROR_300};
            border-radius: 8px;
            margin-top: 8px;
            .ant-alert-message {
              color: ${colors.ERROR_300};
            }
            .ant-alert-icon {
              color: ${colors.ERROR_300};
            }
          `}
        />
      )}
    </div>
  )
}

export default EditAccountModalContent
